/**
 * The sidebar as a slide-over drawer on phones and tablets (<1024px), opened by
 * the ☰ in the app bar. Modal: focus is trapped inside, Escape / the scrim /
 * swiping it left close it, and any navigation closes it. Stays mounted through
 * its exit animation, like Sheet.
 */
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from '../lib/hooks.js';
import { drawerOpen, useDrawerOpen } from '../lib/ui-state.js';
import { pushOverlay } from '../lib/overlays.js';
import { SidebarContent } from './Sidebar.js';

const EXIT_MS = 240;
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

const close = (): void => drawerOpen.set(false);

export function NavDrawer(): ReactNode {
  const open = useDrawerOpen();
  const desktop = useMediaQuery('(min-width: 1024px)');
  const loc = useLocation();
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);
  const panel = useRef<HTMLElement>(null);
  const restore = useRef<HTMLElement | null>(null);
  const drag = useRef<{ x: number; y: number; dx: number; t: number; locked: boolean } | null>(null);

  // Any navigation (link, home tab, back gesture) closes it.
  const dragged = useRef(false);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    close();
  }, [loc.key]);

  // Growing into the desktop layout replaces it with the real sidebar.
  useEffect(() => {
    if (desktop) close();
  }, [desktop]);

  useEffect(() => {
    if (open) {
      restore.current = document.activeElement as HTMLElement | null;
      setMounted(true);
      setClosing(false);
      return undefined;
    }
    if (!mounted) return undefined;
    setClosing(true);
    const t = window.setTimeout(() => {
      setMounted(false);
      setClosing(false);
      restore.current?.focus?.({ preventScroll: true });
    }, EXIT_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Layout effect: the listeners must be on before the first touch can land.
  useLayoutEffect(() => {
    if (!mounted || closing) return undefined;
    const p = panel.current;
    const current = p?.querySelector<HTMLElement>('.side-link.active, [aria-selected="true"]');
    (current ?? p)?.focus({ preventScroll: true });
    const onKey = (e: globalThis.KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    const pop = pushOverlay(close);

    // Swipe left to close. Touch events rather than pointer events: the browser
    // cancels pointers once the list starts scrolling, and only a non-passive
    // touchmove can claim the drag from it.
    const onStart = (e: TouchEvent): void => {
      const t = e.touches[0];
      dragged.current = false;
      drag.current = e.touches.length === 1 && t ? { x: t.clientX, y: t.clientY, dx: 0, t: e.timeStamp, locked: false } : null;
    };
    const onMove = (e: TouchEvent): void => {
      const d = drag.current;
      const t = e.touches[0];
      if (!d || !p || !t) return;
      const dx = t.clientX - d.x;
      const dy = t.clientY - d.y;
      if (!d.locked) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        if (Math.abs(dy) > Math.abs(dx) || dx > 0) {
          drag.current = null; // a scroll, or a pull the wrong way
          return;
        }
        d.locked = true;
        dragged.current = true;
        p.dataset.dragging = 'true';
      }
      if (e.cancelable) e.preventDefault();
      d.dx = Math.min(0, dx);
      p.style.transform = `translate3d(${d.dx}px, 0, 0)`;
    };
    const onEnd = (e: TouchEvent): void => {
      const d = drag.current;
      drag.current = null;
      if (!d?.locked || !p) return;
      delete p.dataset.dragging;
      const speed = -d.dx / Math.max(1, e.timeStamp - d.t);
      if (-d.dx > p.offsetWidth * 0.3 || speed > 0.5) close();
      else p.style.transform = '';
      // Most browsers send no click after a drag; clear the guard if this one doesn't.
      window.setTimeout(() => (dragged.current = false), 400);
    };
    p?.addEventListener('touchstart', onStart, { passive: true });
    p?.addEventListener('touchmove', onMove, { passive: false });
    p?.addEventListener('touchend', onEnd);
    p?.addEventListener('touchcancel', onEnd);
    return () => {
      window.removeEventListener('keydown', onKey);
      pop();
      p?.removeEventListener('touchstart', onStart);
      p?.removeEventListener('touchmove', onMove);
      p?.removeEventListener('touchend', onEnd);
      p?.removeEventListener('touchcancel', onEnd);
    };
  }, [mounted, closing]);

  if (!mounted) return null;

  const trap = (e: KeyboardEvent<HTMLElement>): void => {
    if (e.key !== 'Tab' || !panel.current) return;
    const els = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!els.length) return;
    const firstEl = els[0]!;
    const lastEl = els[els.length - 1]!;
    const active = document.activeElement;
    if (e.shiftKey && (active === firstEl || active === panel.current)) {
      e.preventDefault();
      lastEl.focus();
    } else if (!e.shiftKey && active === lastEl) {
      e.preventDefault();
      firstEl.focus();
    }
  };

  // A tap on the link for the page you're already on doesn't navigate; close anyway.
  const onClick = (e: MouseEvent<HTMLElement>): void => {
    if (dragged.current) {
      // The click that ends a swipe isn't a tap on whatever was under the finger.
      dragged.current = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if ((e.target as HTMLElement).closest('a[href]')) close();
  };

  return createPortal(
    <div className="drawer-root" data-closing={closing ? 'true' : undefined}>
      <div className="sheet-scrim drawer-scrim" onClick={close} aria-hidden="true" data-testid="drawer-scrim" />
      <nav
        ref={panel}
        className="nav-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        tabIndex={-1}
        data-testid="nav-drawer"
        onKeyDown={trap}
        onClickCapture={onClick}
      >
        <SidebarContent onClose={close} />
      </nav>
    </div>,
    document.body,
  );
}
